import { samplingApiToForm, samplingApiSelection } from './apiContract';
import {
  PhysicalSamplingConfig,
  ProductOptionRequirement,
  ProductOptionSelection,
  ProductOptionSource,
  ProductSelectionDraft,
  ProductSelectionSnapshot,
  SamplingImage,
  SamplingMode,
  SamplingOption,
  SamplingProduct
} from './types';

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

const asString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const asOptionalString = (value: unknown): string | undefined => {
  const normalized = asString(value);
  return normalized || undefined;
};

const asPrice = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const uniqueStrings = (values: unknown): string[] => {
  const seen = new Set<string>();
  return asArray(values).reduce<string[]>((result, item) => {
    const value = asString(
      typeof item === 'object' && item !== null
        ? (item as Record<string, unknown>).value
        : item
    );
    const key = value.toLocaleLowerCase();
    if (!value || seen.has(key)) return result;
    seen.add(key);
    result.push(value);
    return result;
  }, []);
};

const normalizeImages = (value: unknown): SamplingImage[] =>
  asArray(value).reduce<SamplingImage[]>((result, item) => {
    if (typeof item === 'string') {
      const src = asString(item);
      if (src) result.push({ src });
      return result;
    }
    const image = asRecord(item);
    const src = asString(image.src ?? image.url);
    if (!src) return result;
    result.push({
      src,
      fileName: asOptionalString(image.fileName ?? image.name),
      ...(image.type === 'video' ? { type: 'video' as const } : {})
    });
    return result;
  }, []);

const normalizeOption = (value: unknown, index: number): SamplingOption => {
  const option = asRecord(value);
  return {
    id: asString(option.id) || `option-${index + 1}`,
    name: asString(option.name ?? option.label),
    allowedValues: uniqueStrings(option.allowedValues ?? option.values),
    selectionGuide: asOptionalString(
      option.selectionGuide ?? option.description
    ),
    ...(option.selectionGuideFormat || option.descriptionFormat
      ? {
          selectionGuideFormat:
            option.selectionGuideFormat === 'html' ||
            option.descriptionFormat === 'html'
              ? ('html' as const)
              : ('text' as const)
        }
      : {}),
    selectionGuideImages: normalizeImages(
      option.selectionGuideImages ?? option.images
    )
  };
};

const normalizeProduct = (value: unknown, index: number): SamplingProduct => {
  const product = asRecord(value);
  const options = asArray(product.options).map(normalizeOption);
  const optionRequirement =
    product.optionRequirement === ProductOptionRequirement.REQUIRED ||
    options.length
      ? ProductOptionRequirement.REQUIRED
      : ProductOptionRequirement.NONE;
  const optionSource =
    optionRequirement === ProductOptionRequirement.NONE
      ? undefined
      : product.optionSource === ProductOptionSource.PRODUCT_PAGE
        ? ProductOptionSource.PRODUCT_PAGE
        : ProductOptionSource.MANUAL;

  return {
    id: asString(product.id) || `product-${index + 1}`,
    ...(asString(product.serverProductId)
      ? { serverProductId: asString(product.serverProductId) }
      : {}),
    name: asString(product.name),
    imageUrl: asString(product.imageUrl),
    imageFileName: asOptionalString(product.imageFileName),
    productUrl: asString(product.productUrl),
    retailPriceUsd: asPrice(product.retailPriceUsd),
    optionRequirement,
    optionSource,
    options:
      optionRequirement === ProductOptionRequirement.REQUIRED ? options : [],
    productPageSelectionGuide: asOptionalString(
      product.productPageSelectionGuide
    ),
    ...(product.productPageSelectionGuideFormat
      ? {
          productPageSelectionGuideFormat:
            product.productPageSelectionGuideFormat === 'html'
              ? ('html' as const)
              : ('text' as const)
        }
      : {}),
    ...(Array.isArray(product.productPageSelectionGuideImages)
      ? {
          productPageSelectionGuideImages: normalizeImages(
            product.productPageSelectionGuideImages
          )
        }
      : {}),
    brandName: asOptionalString(product.brandName),
    brandLogoUrl: asOptionalString(product.brandLogoUrl)
  };
};

const clampSelectionLimit = (
  value: unknown,
  count: number,
  mode: SamplingMode
): number => {
  if (mode === SamplingMode.SINGLE_PRODUCT) return 1;
  const parsed = Math.trunc(Number(value));
  if (!Number.isFinite(parsed)) return 1;
  return Math.min(Math.max(parsed, 1), Math.max(count, 1));
};

export const normalizePhysicalSamplingConfig = (
  value: unknown
): PhysicalSamplingConfig | null => {
  const product = asRecord(value);
  const physicalSampling = asRecord(
    product.physicalSampling ?? samplingApiToForm(product)
  );
  if (physicalSampling.isUnsupported === true)
    return {
      mode: SamplingMode.SINGLE_PRODUCT,
      products: [],
      maxSelectableProducts: 1,
      isUnsupported: true
    };
  if (Array.isArray(physicalSampling.products)) {
    const mode =
      physicalSampling.mode === SamplingMode.MULTIPLE_PRODUCTS
        ? SamplingMode.MULTIPLE_PRODUCTS
        : SamplingMode.SINGLE_PRODUCT;
    const products = physicalSampling.products.map((item, index) => ({
      ...normalizeProduct(item, index),
      brandName: asOptionalString(
        asRecord(item).brandName ?? product.brandName ?? product.name
      ),
      brandLogoUrl: asOptionalString(
        asRecord(item).brandLogoUrl ?? product.productLogo ?? product.logoUrl
      )
    }));
    const normalizedProducts =
      mode === SamplingMode.SINGLE_PRODUCT ? products.slice(0, 1) : products;
    return {
      mode,
      maxSelectableProducts: clampSelectionLimit(
        physicalSampling.maxSelectableProducts,
        normalizedProducts.length,
        mode
      ),
      products: normalizedProducts,
      ...(physicalSampling.isLegacy === true ? { isLegacy: true } : {})
    };
  }

  const legacyOptions = asArray(product.skuList).map(normalizeOption);
  const hasLegacyProduct = Boolean(
    asString(product.physicalProductName) ||
      asString(product.physicalProductPhoto) ||
      asString(product.physicalProductLink) ||
      product.physicalProductCashValue !== undefined ||
      legacyOptions.length
  );
  if (!hasLegacyProduct) return null;

  return {
    mode: SamplingMode.SINGLE_PRODUCT,
    maxSelectableProducts: 1,
    isLegacy: true,
    products: [
      {
        id: 'legacy-product',
        name: asString(product.physicalProductName),
        imageUrl: asString(product.physicalProductPhoto),
        imageFileName: asOptionalString(product.physicalProductPhotoName),
        productUrl: asString(product.physicalProductLink),
        retailPriceUsd: asPrice(product.physicalProductCashValue),
        optionRequirement: legacyOptions.length
          ? ProductOptionRequirement.REQUIRED
          : ProductOptionRequirement.NONE,
        optionSource: legacyOptions.length
          ? ProductOptionSource.MANUAL
          : undefined,
        options: legacyOptions
      }
    ]
  };
};

const normalizeOptionSelections = (value: unknown): ProductOptionSelection[] =>
  asArray(value).reduce<ProductOptionSelection[]>((result, item) => {
    const selection = asRecord(item);
    const optionName = asString(selection.optionName ?? selection.label);
    const selectedValue = asString(selection.value);
    if (!optionName || !selectedValue) return result;
    result.push({
      optionId: asOptionalString(selection.optionId),
      optionName,
      value: selectedValue
    });
    return result;
  }, []);

const hasProductSnapshot = (value: unknown): boolean => {
  const product = asRecord(value);
  return Boolean(asString(product.name) || asString(product.imageUrl));
};

const normalizeSelection = (
  value: unknown
): ProductSelectionSnapshot | null => {
  const selection = asRecord(value);
  if (!Array.isArray(selection.products)) return null;
  const products = selection.products.reduce<
    ProductSelectionSnapshot['products']
  >((result, item, index) => {
    const selected = asRecord(item);
    const optionSelections = normalizeOptionSelections(
      selected.optionSelections
    );
    const productPageSelection = asOptionalString(
      selected.productPageSelection
    );
    const snapshot = hasProductSnapshot(selected.productSnapshot)
      ? normalizeProduct(selected.productSnapshot, index)
      : undefined;
    if (!snapshot && !optionSelections.length && !productPageSelection)
      return result;
    result.push({
      productId:
        asString(selected.productId) ||
        snapshot?.id ||
        `selected-product-${index + 1}`,
      productSnapshot: snapshot,
      optionSelections,
      productPageSelection
    });
    return result;
  }, []);
  return products.length
    ? { selectedAt: asOptionalString(selection.selectedAt), products }
    : null;
};

/** Read frozen Address payload first. Never reconstruct history from current campaign data. */
export const normalizeProductSelectionSnapshot = (
  order: unknown,
  _config?: PhysicalSamplingConfig | null,
  addressPayload?: unknown
): ProductSelectionSnapshot | null => {
  const orderRecord = asRecord(order);
  const contents = asArray(orderRecord.contents);
  const latestAddress = [...contents]
    .reverse()
    .find((item) => asRecord(item).stage === 'Address');
  const historical = addressPayload !== undefined;
  const payload = asRecord(
    historical ? addressPayload : asRecord(latestAddress).payload
  );
  const productDetails = asRecord(payload.productDetails);
  const frozen =
    normalizeSelection(payload.productSelection) ??
    normalizeSelection(productDetails.productSelection) ??
    normalizeSelection(samplingApiSelection(productDetails)) ??
    normalizeSelection(productDetails);
  if (frozen) return frozen;
  const canonical = historical
    ? null
    : (normalizeSelection(samplingApiSelection(orderRecord)) ??
      normalizeSelection(orderRecord.productSelection));
  if (canonical) return canonical;

  const legacySelections = normalizeOptionSelections(
    Array.isArray(productDetails.fields)
      ? productDetails.fields
      : historical
        ? []
        : orderRecord.skuList
  );
  const productPageSelection = asOptionalString(
    productDetails.optionsOnPageSelection ??
      (historical
        ? undefined
        : orderRecord.physicalProductOptionsOnPageSelection)
  );
  if (!legacySelections.length && !productPageSelection) return null;
  return {
    products: [
      {
        productId: 'legacy-product',
        optionSelections: legacySelections,
        productPageSelection
      }
    ]
  };
};

export const canUseLegacyProductSelection = (
  config: PhysicalSamplingConfig
): boolean =>
  Boolean(
    config.isLegacy &&
      config.mode === SamplingMode.SINGLE_PRODUCT &&
      config.products[0]?.optionSource === ProductOptionSource.MANUAL
  );

export const hasPersistedProductSelection = (
  config: PhysicalSamplingConfig,
  draft: ProductSelectionDraft,
  refreshedDetail: unknown
): boolean => {
  const order = asRecord(asRecord(refreshedDetail).order);
  if (draft.selectedProductIds.length === 0) return false;
  // A previous Address submission is history, not proof of the new write.
  const snapshot = normalizeProductSelectionSnapshot({
    ...order,
    contents: []
  });
  if (!snapshot) return false;
  const expected = serializeProductSelectionDraft(
    config,
    draft
  ).productSelection;
  return (
    expected.products.every((product) => {
      const saved = snapshot.products.find(
        (item) => item.productId === product.productId
      );
      return Boolean(
        saved &&
          saved.productPageSelection === product.productPageSelection &&
          saved.optionSelections.length === product.optionSelections.length &&
          product.optionSelections.every((option) =>
            saved.optionSelections.some(
              (item) =>
                item.optionName === option.optionName &&
                item.value === option.value
            )
          )
      );
    }) && snapshot.products.length === expected.products.length
  );
};

const emptyDraftValue = () => ({
  optionSelections: {},
  productPageSelection: ''
});

export const createProductSelectionDraft = (
  config: PhysicalSamplingConfig,
  snapshot?: ProductSelectionSnapshot | null
): ProductSelectionDraft => {
  const selectedProducts = snapshot?.products ?? [];
  const selectedProductIds = selectedProducts.length
    ? selectedProducts.map((product) => product.productId)
    : config.mode === SamplingMode.SINGLE_PRODUCT && config.products[0]
      ? [config.products[0].id]
      : [];

  const valuesByProductId = config.products.reduce<
    ProductSelectionDraft['valuesByProductId']
  >((result, product) => {
    const selected = selectedProducts.find(
      (item) => item.productId === product.id
    );
    result[product.id] = selected
      ? {
          optionSelections: selected.optionSelections.reduce<
            Record<string, string>
          >((values, option) => {
            const optionDefinition = product.options.find(
              (item) =>
                (option.optionId && item.id === option.optionId) ||
                item.name === option.optionName
            );
            if (optionDefinition) values[optionDefinition.id] = option.value;
            return values;
          }, {}),
          productPageSelection: selected.productPageSelection ?? ''
        }
      : emptyDraftValue();
    return result;
  }, {});

  return { selectedProductIds, valuesByProductId };
};

export const serializeProductSelectionDraft = (
  config: PhysicalSamplingConfig,
  draft: ProductSelectionDraft
): {
  productSelection: ProductSelectionSnapshot;
  skuList: Array<{ label: string; value: string }>;
} => {
  const selectedProducts = draft.selectedProductIds.reduce<
    ProductSelectionSnapshot['products']
  >((result, productId) => {
    const product = config.products.find((item) => item.id === productId);
    if (!product) return result;
    const values = draft.valuesByProductId[productId] ?? emptyDraftValue();
    const optionSelections = product.options.reduce<ProductOptionSelection[]>(
      (selections, option) => {
        const value = values.optionSelections[option.id]?.trim();
        if (value) {
          selections.push({
            optionId: option.id,
            optionName: option.name,
            value
          });
        }
        return selections;
      },
      []
    );
    result.push({
      productId,
      productSnapshot: product,
      optionSelections,
      productPageSelection: values.productPageSelection.trim() || undefined
    });
    return result;
  }, []);
  const snapshot = { products: selectedProducts };
  const firstSelection = selectedProducts[0];
  return {
    productSelection: snapshot,
    skuList:
      config.mode === SamplingMode.SINGLE_PRODUCT && firstSelection
        ? firstSelection.optionSelections.map((option) => ({
            label: option.optionName,
            value: option.value
          }))
        : []
  };
};

/** Service-defined worth-up-to is display-only, separate from every cash quote. */
export const getSamplingValueSummary = (value: unknown) => {
  const product = asRecord(value);
  const amount = product.physicalProductWorthUpTo;
  return typeof amount === 'number' && Number.isFinite(amount) && amount >= 0
    ? {
        amountUsd: amount,
        isUpperBound: product.physicalProductSamplingMethod === 'MULTIPLE'
      }
    : undefined;
};
